/**
 * Guardian Invite Acceptance Page
 *
 * URL: /invite/guardian?token=xxx
 *
 * Flow:
 * 1. Load invite info (unauthenticated)
 * 2. If not logged in: show magic link form
 * 3. If logged in: accept the invite automatically
 * 4. On success: redirect to guardian portal
 */

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthContext } from "@/lib/auth-context";
import { supabase } from "@/supabase/client";

const CORE_API = import.meta.env.VITE_CORE_API_URL || "";

interface InviteInfo {
  student_first_name: string;
  teacher_name: string | null;
  classroom_name: string | null;
  status: string;
  expired: boolean;
}

interface AcceptResult {
  student_id: string;
  student_name: string;
  classroom_name: string | null;
}

type PageState =
  | "loading"
  | "show_invite"
  | "magic_link_form"
  | "magic_link_sent"
  | "accepting"
  | "accepted"
  | "error";

export default function GuardianInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { session } = useAuthContext();
  const token = searchParams.get("token") || "";

  const [state, setState] = useState<PageState>("loading");
  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [acceptResult, setAcceptResult] = useState<AcceptResult | null>(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [email, setEmail] = useState("");
  const [emailSending, setEmailSending] = useState(false);

  // 1. Load invite info
  useEffect(() => {
    if (!token) {
      setErrorMsg("No invite token provided. Please check your invite link.");
      setState("error");
      return;
    }

    (async () => {
      try {
        const res = await fetch(
          `${CORE_API}/api/v1/guardian/invite-info?token=${encodeURIComponent(token)}`
        );
        const body = await res.json();

        if (!res.ok || !body.data) {
          setErrorMsg(body?.error?.message || "Invite not found.");
          setState("error");
          return;
        }

        const info = body.data as InviteInfo;
        setInviteInfo(info);

        if (info.expired) {
          setErrorMsg("This invite has expired. Please ask the teacher to resend it.");
          setState("error");
          return;
        }

        if (info.status === "accepted") {
          setErrorMsg("This invite has already been accepted. You can go to your guardian portal.");
          setState("error");
          return;
        }

        setState("show_invite");
      } catch {
        setErrorMsg("Unable to load invite. Please try again.");
        setState("error");
      }
    })();
  }, [token]);

  // 2. If logged in and invite is loaded, auto-accept
  useEffect(() => {
    if (state !== "show_invite" || !session?.access_token) return;
    acceptInvite();
  }, [state, session?.access_token]);

  async function acceptInvite() {
    if (!session?.access_token || !token) return;
    setState("accepting");

    try {
      const res = await fetch(`${CORE_API}/api/v1/guardian/accept-invite`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ token }),
      });

      const body = await res.json();

      if (!res.ok) {
        const code = body?.error?.code;
        if (code === "ALREADY_ACCEPTED") {
          setState("accepted");
          setAcceptResult({
            student_id: "",
            student_name: inviteInfo?.student_first_name || "your student",
            classroom_name: inviteInfo?.classroom_name || null,
          });
          return;
        }
        setErrorMsg(
          body?.error?.message || "Unable to accept invite. Please try again."
        );
        setState("error");
        return;
      }

      setAcceptResult(body.data);
      setState("accepted");
    } catch {
      setErrorMsg("Unable to accept invite. Please try again.");
      setState("error");
    }
  }

  async function handleSendMagicLink(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;

    setEmailSending(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: email.trim().toLowerCase(),
        options: {
          emailRedirectTo: `${window.location.origin}/invite/guardian?token=${encodeURIComponent(token)}`,
        },
      });

      if (error) {
        setErrorMsg(error.message);
        setState("error");
        return;
      }

      setState("magic_link_sent");
    } catch {
      setErrorMsg("Unable to send magic link. Please try again.");
      setState("error");
    } finally {
      setEmailSending(false);
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="flex min-h-screen items-center justify-center bg-linen p-4">
      <div className="w-full max-w-md rounded-3xl border border-sand bg-white/90 p-8 shadow-soft">
        {/* Header */}
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold text-cocoa">Project Tower</h1>
          <p className="mt-1 text-sm text-cocoa/60">Guardian Portal</p>
        </div>

        {/* Loading */}
        {state === "loading" && (
          <div className="text-center">
            <p className="text-cocoa/70">Loading invite...</p>
          </div>
        )}

        {/* Show invite (not logged in) */}
        {state === "show_invite" && !session?.access_token && inviteInfo && (
          <div className="space-y-6">
            <div className="rounded-2xl border border-mint/30 bg-mint/10 p-4 text-center">
              <p className="text-sm text-cocoa/80">You've been invited to connect with</p>
              <p className="mt-1 text-lg font-semibold text-cocoa">
                {inviteInfo.student_first_name}'s classroom
              </p>
              {inviteInfo.teacher_name && (
                <p className="mt-1 text-sm text-cocoa/60">
                  by {inviteInfo.teacher_name}
                  {inviteInfo.classroom_name ? ` at ${inviteInfo.classroom_name}` : ""}
                </p>
              )}
            </div>

            <div className="space-y-3">
              <p className="text-sm text-cocoa/70">
                Sign in with the email your invite was sent to:
              </p>
              <form onSubmit={handleSendMagicLink} className="space-y-3">
                <input
                  type="email"
                  className="input w-full"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                />
                <button
                  type="submit"
                  className="btn w-full"
                  disabled={emailSending || !email.trim()}
                >
                  {emailSending ? "Sending..." : "Send magic link"}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Show invite (logged in — auto-accepting) */}
        {state === "show_invite" && session?.access_token && (
          <div className="text-center">
            <p className="text-cocoa/70">Accepting invite...</p>
          </div>
        )}

        {/* Accepting */}
        {state === "accepting" && (
          <div className="text-center">
            <p className="text-cocoa/70">Connecting you to your student's classroom...</p>
          </div>
        )}

        {/* Magic link sent */}
        {state === "magic_link_sent" && (
          <div className="space-y-4 text-center">
            <div className="rounded-2xl border border-mint/30 bg-mint/10 p-4">
              <p className="text-lg font-semibold text-cocoa">Check your email!</p>
              <p className="mt-2 text-sm text-cocoa/70">
                We sent a sign-in link to <strong>{email}</strong>. Click the link in the
                email, and you'll be brought back here to complete the connection.
              </p>
            </div>
            <button
              type="button"
              className="text-sm text-mint underline"
              onClick={() => setState("show_invite")}
            >
              Use a different email
            </button>
          </div>
        )}

        {/* Accepted */}
        {state === "accepted" && (
          <div className="space-y-6 text-center">
            <div className="rounded-2xl border border-mint/30 bg-mint/10 p-4">
              <p className="text-lg font-semibold text-cocoa">You're connected!</p>
              <p className="mt-2 text-sm text-cocoa/70">
                You are now linked as a guardian for{" "}
                <strong>{acceptResult?.student_name || inviteInfo?.student_first_name}</strong>
                {acceptResult?.classroom_name
                  ? ` in ${acceptResult.classroom_name}`
                  : ""}
                .
              </p>
            </div>
            <button
              type="button"
              className="btn w-full"
              onClick={() => navigate("/portal/guardian")}
            >
              Go to your portal
            </button>
          </div>
        )}

        {/* Error */}
        {state === "error" && (
          <div className="space-y-4 text-center">
            <div className="rounded-2xl border border-brick/30 bg-brick/10 p-4">
              <p className="text-sm text-brick">{errorMsg}</p>
            </div>
            {errorMsg.includes("already been accepted") && (
              <button
                type="button"
                className="btn w-full"
                onClick={() => navigate("/portal/guardian")}
              >
                Go to your portal
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
